import {
  ScorecardEvaluationQuestion,
  ShortScorcardType,
} from "../pages/call-details/RecordingScorecardTab";
import {
  Comment,
  InsertRecordingComment,
  Recording,
  RecordingMoment,
  RecordingSegmentSentiment,
  RecordingTopic,
  RecordingTranscription,
  UpdateRecordingComment,
} from "../types/recording";
import { RecordingLockedSegment } from "../types/recording/RecordingLockedSegment";
import { RecordingScorecard } from "../types/recording/RecordingScorecard";
import { axiosForShare, axiosWithAuth, axiosWithHiHiAuth } from "../utils/customAxios";
import { RecordingPeaks } from "../types/recording/RecordingPeaks";
import { RecordingCallDetailValues } from "../types/recording/RecordingCallDetailValues";
import { RecordingSummary } from "../types/recording/RecordingSummary";
import { RecordingAction } from "../types/recording/RecordingAction";
import { RecordingLockedSegmentAPI } from "../types/recording/RecordingLockedSegmentsAPI";
import { ExportColumnOptions } from "../types/RecordingExportColumnOptions";

const recordingsService = {
  fetchRecording: async (
    id: number,
    shared: boolean = false
  ): Promise<Recording> => {
    try {
      const axiosInstance = shared === true ? axiosForShare : axiosWithAuth;
      const response = await axiosInstance.get(`/recording/${id}`);
      const recording: Recording = response.data;

      return recording;
    } catch (err) {
      throw err;
    }
  },

  fetchRecordingCallDetailValues: async (
    id: number,
    shared: boolean = false,
    hihiAuth: boolean = false,
  ): Promise<RecordingCallDetailValues> => {
    try {
      const axiosInstance = hihiAuth ? axiosWithHiHiAuth : shared === true ? axiosForShare : axiosWithAuth;
      const response = await axiosInstance.get(`/recording/${id}/calldetailvalues`);

      const calldetailValues = response.data;

      return calldetailValues;
    } catch (err) {
      throw err;
    }
  },

  fetchRecordingTranscription: async (
    id: number,
    shared: boolean = false
  ): Promise<RecordingTranscription> => {
    try {
      const axiosInstance = shared === true ? axiosForShare : axiosWithAuth;
      const response = await axiosInstance.get(`/recording/${id}/transcript`);
      const transcription: RecordingTranscription = response.data;

      return transcription;
    } catch (err) {
      throw err;
    }
  },

  fetchLockedSegments: async (id: number, shared: boolean = false): Promise<RecordingLockedSegment[]> => {
    try {
      const axiosInstance = shared === true ? axiosForShare : axiosWithAuth;
      const response = await axiosInstance.get(`/recording/${id}/lockedSegments`);
      const segments: RecordingLockedSegment[] = response.data;

      return segments;
    } catch (err) {
      throw err;
    }
  },

  lockRecordingSegment: async (id: number, lockedSegments: RecordingLockedSegmentAPI[], shared: boolean = false) => {
    try {
      const axiosInstance = shared === true ? axiosForShare : axiosWithAuth;
      const response = await axiosInstance.put(`/recording/${id}/lock`, lockedSegments);
      const message = response.data;
      return message;
    } catch (err) {
      throw err;
    }
  },

  unlockRecordingSegment: async (id: number, lockedSegments: RecordingLockedSegmentAPI[], shared: boolean = false) => {
    try {
      const axiosInstance = shared === true ? axiosForShare : axiosWithAuth;
      const response = await axiosInstance.put(`/recording/${id}/unlock`, lockedSegments);
      const message = response.data;
      return message;
    } catch (err) {
      throw err;
    }
  },
  fetchRecordingComments: async (
    recordingId: number,
    shared: boolean = false
  ): Promise<Comment[]> => {
    try {
      const axiosInstance = shared === true ? axiosForShare : axiosWithAuth;
      const response = await axiosInstance.get(
        `/recording/${recordingId}/comment`
      );
      const comments: Comment[] = response.data;
      return comments;
    } catch (err) {
      throw err;
    }
  },

  addRecordingComment: async (
    recordingId: number,
    newComment: InsertRecordingComment,
    shared: boolean = false
  ): Promise<Comment> => {
    try {
      const axiosInstance = shared === true ? axiosForShare : axiosWithAuth;
      const response = await axiosInstance.post(
        `/recording/${recordingId}/comment`,
        newComment
      );
      const createdComment: Comment = response.data;
      return createdComment;
    } catch (err) {
      throw err;
    }
  },

  updateRecordingComment: async (
    recordingId: number,
    commentId: number,
    editedComment: UpdateRecordingComment,
    shared: boolean = false
  ): Promise<Comment> => {
    try {
      const axiosInstance = shared === true ? axiosForShare : axiosWithAuth;
      const response = await axiosInstance.put(
        `/recording/${recordingId}/comment`,
        editedComment
      );
      const updatedComment: Comment = response.data;
      return updatedComment;
    } catch (err) {
      throw err;
    }
  },

  deleteRecordingComment: async (
    recordingId: number,
    commentId: number,
    shared: boolean = false
  ): Promise<void> => {
    try {
      const axiosInstance = shared === true ? axiosForShare : axiosWithAuth;
      const response = await axiosInstance.delete(
        `/recording/${recordingId}/comment/${commentId}`
      );
      if (response.data <= 0) {
        throw Error("Recording comment could not be deleted!");
      }
    } catch (err) {
      throw err;
    }
  },

  retainRecording: async (recordingId: number, isMarkedForRetention: boolean, shared: boolean): Promise<boolean> => {
    try {
      const axiosInstance = shared === true ? axiosForShare : axiosWithAuth;
      const response = await axiosInstance.put(`/recording/${recordingId}/retention`
        + `?isMarkedForRetention=${isMarkedForRetention}`);
      const isRetained: boolean = response.data;

      return isRetained;
    } catch (err) {
      throw err;
    }
  },

  deleteRecording: async (recordingId: number, shared: boolean): Promise<boolean> => {
    try {
      const axiosInstance = shared === true ? axiosForShare : axiosWithAuth;
      const response = await axiosInstance.put(`/recording/${recordingId}/deletion`);
      const isDeleted: boolean = response.data;

      return isDeleted;
    } catch (err) {
      throw err;
    }
  },

  fetchRecordingScorecard: async (
    recordingId: number,
    shared: boolean = false
  ): Promise<RecordingScorecard> => {
    try {
      const axiosInstance = shared === true ? axiosForShare : axiosWithAuth;
      const response = await axiosInstance.get(
        `/recording/${recordingId}/scorecard`
      );
      const scorecard: RecordingScorecard = response.data;

      return scorecard;
    } catch (err) {
      throw err;
    }
  },

  addRecordingScorecard: async (
    recordingId: number,
    scorecard: ShortScorcardType,
    evaluation: ScorecardEvaluationQuestion[],
    shared: boolean = false,
    state: "insert" | "update",
    autoQAScore: number | null,
  ) => {
    try {
      let score = 0;
      evaluation.forEach((e) => {
        if (e.questionScore != null) {
          score += e.questionScore
        }
      })
      score = score / evaluation.length;
      const recordingScorecard: RecordingScorecard = {
        scorecardName: { id: scorecard.id, name: scorecard.text },
        score: score,
        scoreDetails: evaluation.map((e) => {
          return {
            id: e.recordingScorecardDetailId ?? 0,
            scorecardGroupName: e.category,
            question: { id: e.id, questionText: e.question, weightage: 0 },
            relevant: 0,
            questionScore: e.questionScore != null ? e.questionScore : null,
            autoQAQuestionScore: e.autoQAQuestionScore != null ? e.autoQAQuestionScore : null,
            questionEvaluatedBy: e.questionEvaluatedBy
          };
        }),
        autoQAScore: autoQAScore != null ? autoQAScore : null
      };

      const axiosInstance = shared === true ? axiosForShare : axiosWithAuth;
      const response = state === "insert" ? await axiosInstance.post(
        `/recording/${recordingId}/scorecard`,
        recordingScorecard
      ) : await axiosInstance.put(
        `/recording/${recordingId}/scorecard`,
        recordingScorecard
      )
      const createdRecordingScorecard: RecordingScorecard = response.data;
      return createdRecordingScorecard;
    } catch (err) {
      throw err;
    }
  },

  deleteRecordingScorecard: async (recordingId: number, shared: boolean = false): Promise<any> => {
    try {
      const axiosInstance = shared === true ? axiosForShare : axiosWithAuth;
      const response = await axiosInstance.delete(
        `/recording/${recordingId}/scorecard`
      );
      return response;
    }
    catch (err) {
      throw err;
    }
  },

  fetchRecordingMoments: async (
    recordingId: number,
    shared: boolean = false
  ): Promise<RecordingMoment[]> => {
    try {
      const axiosInstance = shared === true ? axiosForShare : axiosWithAuth;
      const response = await axiosInstance.get(
        `/recording/${recordingId}/moment`
      );
      const moments: RecordingMoment[] = response.data;

      return moments;
    } catch (err) {
      throw err;
    }
  },

  fetchRecordingTopics: async (
    recordingId: number,
    shared: boolean = false
  ): Promise<RecordingTopic[]> => {
    try {
      const axiosInstance = shared === true ? axiosForShare : axiosWithAuth;
      const response = await axiosInstance.get(
        `/recording/${recordingId}/topic`
      );
      const topics: RecordingTopic[] = response.data;

      return topics;
    } catch (err) {
      throw err;
    }
  },

  fetchRecordingSegmentSentiments: async (
    recordingId: number,
    shared: boolean = false
  ): Promise<RecordingSegmentSentiment[]> => {
    try {
      const axiosInstance = shared === true ? axiosForShare : axiosWithAuth;
      const response = await axiosInstance.get(
        `/recording/${recordingId}/segmentsentiment`
      );
      const segmentSentiments: RecordingSegmentSentiment[] = response.data;

      return segmentSentiments;
    } catch (err) {
      throw err;
    }
  },

  fetchRecordingPeaks: async (
    recordingId: number,
    shared: boolean = false,
    hihiAuth: boolean = false
  ): Promise<RecordingPeaks | string> => {
    try {
      const axiosInstance = hihiAuth ? axiosWithHiHiAuth : shared === true ? axiosForShare : axiosWithAuth;
      const response = await axiosInstance.get(
        `/recording/${recordingId}/peaks`
      );
      const peaks: RecordingPeaks = response.data;

      return peaks;
    } catch (err) {
      throw err;
    }
  },

  fetchRecordingSummary: async (
    recordingId: number,
    shared: boolean = false
  ): Promise<RecordingSummary> => {
    try {
      const axiosInstance = shared === true ? axiosForShare : axiosWithAuth;
      const response = await axiosInstance.get(
        `/recording/${recordingId}/summary`
      );

      const summary: RecordingSummary = response.data;
      return summary;
    } catch (err) {
      throw err;
    }
  },

  fetchRecordingActions: async (
    id: number,
    shared: boolean = false
  ): Promise<RecordingAction[]> => {
    try {
      const axiosInstance = shared === true ? axiosForShare : axiosWithAuth;
      const response = await axiosInstance.get(`/recording/${id}/Action`);
      const actions: RecordingAction[] = response.data;

      return actions;
    } catch (err) {
      throw err;
    }
  },

  updateRecordingAction: async (
    recordingId: number,
    recordingActionId: number,
    state?: string,
    shared: boolean = false
  ): Promise<any> => {
    try {
      const axiosInstance = shared === true ? axiosForShare : axiosWithAuth;
      const response = await axiosInstance.put(`/recording/${recordingId}/Action/${recordingActionId}?state=${state}`);
      return response;
    }
    catch (err) {
      throw err;
    }
  },

  exportRecordingsByIds: async (
    columns: ExportColumnOptions,
    selectedRecordingIds: number[]
  ): Promise<{ blob: Blob }> => {
    try {
      const response = await axiosWithAuth.post(
        "/Recording/exportByIds",
        {
          Request: {
            Columns: {
              isDateTime: columns.isDateTime,
              isRecordedUser: columns.isRecordedUser,
              isParticipants: columns.isParticipants,
              isDuration: columns.isDuration,
              isFlag: columns.isFlag,
              isEvaluation: columns.isEvaluation,
              isSentiments: columns.isSentiments,
              isTopics: columns.isTopics,
              isNumberOfComments: columns.isNumberOfComments,
              isCallOutcome: columns.isCallOutcome,
              isReasonCode: columns.isReasonCode,
              isNotes: columns.isNotes
            }
          },
          RecordingIds: selectedRecordingIds,
        },
        { responseType: "blob" }
      );
      return { blob: response.data };
    } catch (err) {
      throw err;
    }
  },

  exportRecordingsBySearchFilters: async (
    columns: ExportColumnOptions,
    requestQuery: string
  ): Promise<{ blob: Blob }> => {
    try {
      const response = await axiosWithAuth.post(
        `/Recording/exportBySearchFilters${requestQuery}`,
        {
          columns: columns
        },
        { responseType: "blob" }
      );

      return { blob: response.data };
    } catch (err) {
      throw err;
    }
  },
  
  getBulkDownloadLimits: async (): Promise<{
    bulkDownloadSizeLimitInMbs: number;
    bulkDownloadRowCountLimit: number;
  }> => {
    try {
      const response = await axiosWithAuth.get(`/systemsetting/getBulkDownloadLimits`); 
      return response?.data;
    }
    catch (err) {
      throw err;
    }
  },

  bulkdownloadV2Request: async (
    selectedRecordingIds: number[]
  ): Promise<boolean> => {
    try {
      const response = await axiosWithAuth.post(`/Recording/bulkdownloadV2/request`,
        {
          RecordingIds: selectedRecordingIds
        }
      );
      const isSuccessful: boolean = response.data;
      return isSuccessful;
    } catch (err) {
      throw err;
    }
  },

  bulkdownloadV2Downlaod: async (
    requestId: string
  ): Promise<any> => {
    try {
      const response = await axiosWithAuth.get(`/Recording/bulkdownloadV2/${requestId}/download`,
        { responseType: "blob" }
      );
      return response;
    } catch (err) {
      throw err;
    }
  },

};

export default recordingsService;
